import { PrognoseActie, useReplaceActie } from "@/entities/actie";
import { Prognose, PrognoseStatus, PrognoseStatusOptions, useGetDossierDetailsById } from "@/entities/dossier-details";
import { Button, DialogFooter, ErrorDisplay, Icon, Label, Selectfield, Separator } from "@/shared/components";
import { cn, hourFormatInAmsterdam } from "@/shared/utils";
import { ReactNode, useState } from "react";
import { ActieDialog, ActieDialogFooterProps, DeclineActieConfirmation } from "./actie-dialog.tsx";

type PrognoseActieDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dossierId: string;
    actie: PrognoseActie;
};

const determinePrognoseStatus = (prorailStatus: string | undefined, hasCurrentPrognose: boolean): PrognoseStatus => {
    if (Prognose.isPrognoseStatus(prorailStatus)) {
        return prorailStatus;
    }
    return hasCurrentPrognose ? PrognoseStatusOptions.Aangepast : PrognoseStatusOptions.Initieel;
};

export function PrognoseActieDialog({ open, onOpenChange, dossierId, actie }: Readonly<PrognoseActieDialogProps>) {
    const { data } = useGetDossierDetailsById(dossierId);
    const oudePrognose = data?.prognose ?? null;
    const [toelichting, setToelichting] = useState("");
    const [nieuwePrognoseStatus, setNieuwePrognoseStatus] = useState(determinePrognoseStatus(actie.nieuweWaarde.status, !!oudePrognose));

    const updatedActie = PrognoseActie.withStatusAndToelichting(actie, nieuwePrognoseStatus, toelichting);

    const footer = (props: ActieDialogFooterProps<PrognoseActie>) => <PrognoseDialogFooter {...props} actie={updatedActie} />;

    const onOpenChangeWrapper = (open: boolean) => {
        if (!open) {
            // reset toelichting and status
            setToelichting("");
            setNieuwePrognoseStatus(determinePrognoseStatus(actie.nieuweWaarde.status, !!oudePrognose));
        }
        onOpenChange(open);
    };

    return (
        <ActieDialog open={open} onOpenChange={onOpenChangeWrapper} dossierId={dossierId} actie={actie} footer={footer}>
            <PrognoseActieContent
                toelichting={toelichting}
                setToelichting={setToelichting}
                status={nieuwePrognoseStatus}
                setStatus={setNieuwePrognoseStatus}
                actie={actie}
            >
                {oudePrognose && <PrognoseUpdate id="value-old" waarde={hourFormatInAmsterdam(oudePrognose.datumTijd)} label="Oud" />}
                {oudePrognose && <Icon name="arrow-right" className="flex h-full items-end pb-2" data-testid="arrow-right" />}
                <PrognoseUpdate id="value-new" waarde={hourFormatInAmsterdam(actie.nieuweWaarde.datumTijd)} label="Nieuw" />
            </PrognoseActieContent>
        </ActieDialog>
    );
}

type PrognoseActieContentProps = {
    toelichting: string;
    setToelichting: (toelichting: string) => void;
    status: PrognoseStatus;
    setStatus: (status: PrognoseStatus) => void;
    actie: PrognoseActie;
    children: ReactNode;
};

function PrognoseActieContent({ toelichting, setToelichting, status, setStatus, actie, children }: PrognoseActieContentProps) {
    return (
        <div className="grid gap-4">
            <div className="m-auto flex flex-row items-end justify-center gap-4">{children}</div>
            <ProRailStatusEnToelichting status={actie.nieuweWaarde.status} toelichting={actie.nieuweWaarde.toelichting} />
            <Separator orientation="horizontal" />
            <StatusSelectAndToelichting status={status} setStatus={setStatus} toelichting={toelichting} setToelichting={setToelichting} />
        </div>
    );
}

type PrognoseDialogFooterProps = {
    dossierId: string;
    actie: PrognoseActie;
    onClose: () => void;
    className?: string;
};

function PrognoseDialogFooter({ className, dossierId, actie, onClose }: Readonly<PrognoseDialogFooterProps>) {
    const { mutate: replaceActie, error, isPending } = useReplaceActie();
    const [weigerenPopupOpen, setWeigerenPopupOpen] = useState(false);

    const handleAcceptAction = () => {
        replaceActie(
            {
                dossierId,
                actieId: actie.id,
                actie: PrognoseActie.toDto(actie),
            },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <DialogFooter className={cn("flex flex-col justify-between", className)}>
            <div className="flex w-full items-center justify-end gap-2">
                <Button
                    data-testid="decline-actie"
                    variant="tertiary"
                    destructive
                    onClick={() => setWeigerenPopupOpen(true)}
                    disabled={isPending}
                >
                    Weigeren
                </Button>
                <Button data-testid="confirm-actie" variant="primary" onClick={handleAcceptAction} disabled={isPending}>
                    Bevestigen
                </Button>
            </div>
            {error && <ErrorDisplay error={error} />}
            <DeclineActieConfirmation
                actie={actie}
                isOpen={weigerenPopupOpen}
                setIsOpen={setWeigerenPopupOpen}
                closeActieDialog={onClose}
                dossierId={dossierId}
            />
        </DialogFooter>
    );
}

type PrognoseUpdateProps = {
    label: string;
    waarde: string;
    id?: string;
};

function PrognoseUpdate({ label, waarde, id }: Readonly<PrognoseUpdateProps>) {
    return (
        <div className="w-[8ch]">
            <Label htmlFor="value-old" className="text-content-emphasis leading-l text-s font-bold">
                {label}
            </Label>
            <input
                id={id}
                data-testid={id}
                className="bg-elevated-sunken text-m flex w-full min-w-0 rounded-md border px-2.75 py-1.75 shadow-xs"
                value={waarde}
                readOnly
                disabled
            />
        </div>
    );
}

type ProRailStatusEnToelichtingProps = {
    status?: string;
    toelichting?: string;
};

function ProRailStatusEnToelichting({ status, toelichting }: ProRailStatusEnToelichtingProps) {
    return (
        <div className="grid grid-cols-3 gap-x-6 gap-y-1">
            <span className="text-content-emphasis text-s leading-l font-bold">Status ProRail</span>
            <span className="text-content-emphasis text-s leading-l col-span-2 font-bold">Toelichting ProRail</span>
            <span data-testid="prorail-status" className="col-start-1">
                {status || "Geen status"}
            </span>
            <span data-testid="prorail-toelichting" className="col-span-2">
                {toelichting || "Geen toelichting"}
            </span>
            <span className="text-s text-content-subtle leading-l col-span-3">
                Status en toelichting ProRail worden niet getoond in Wissel
            </span>
        </div>
    );
}

const statusOptions = Object.values(PrognoseStatusOptions).map((v) => ({ value: v, label: v }));
type StatusSelectAndToelichtingProps = {
    status: PrognoseStatus;
    setStatus: (status: PrognoseStatus) => void;
    toelichting: string;
    setToelichting: (toelichting: string) => void;
};
function StatusSelectAndToelichting({ status, setStatus, toelichting, setToelichting }: StatusSelectAndToelichtingProps) {
    return (
        <div className="grid grid-cols-[224px_auto] gap-x-4 gap-y-2">
            <Selectfield label="Status" onValueChange={setStatus} value={status} options={statusOptions} name="status" />
            <ActieDialog.Toelichting
                className="grid gap-1"
                toelichting={toelichting}
                setToelichting={setToelichting}
                showToelichtingHelpText={false}
            />
            <div className="text-content-subtle leading-l text-s col-span-2 font-normal">
                Status en toelichting worden na bevestigen in Wissel getoond
            </div>
        </div>
    );
}
